import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Clipboard, Trash2, UploadCloud } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

interface LogoUploaderProps {
	id: string;
	name: string;
	currentLogoUrl: string | null;
	onUpload: (file: File) => Promise<void>;
	isUploading: boolean;
	onPasteClick: () => void;
	onDelete?: () => Promise<void>;
	isDeleting?: boolean;
}

/**
 * A reusable component for uploading and displaying logos
 */
export function LogoUploader({
	id,
	name,
	currentLogoUrl,
	onUpload,
	isUploading,
	onPasteClick,
	onDelete,
	isDeleting,
}: LogoUploaderProps) {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Update selected file
		setSelectedFile(file);

		// Create preview URL
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
	};

	const handleUpload = async () => {
		if (!selectedFile) {
			toast.error("Please select a file");
			return;
		}

		try {
			await onUpload(selectedFile);

			// Clear the selected file and preview URL after successful upload
			setSelectedFile(null);
			setPreviewUrl(null);

			toast.success("Logo updated successfully");
		} catch (error) {
			console.error("Error updating logo:", error);
			toast.error("Failed to update logo");
		}
	};

	const handleDelete = async () => {
		if (!onDelete) return;

		try {
			await onDelete();
			toast.success("Logo deleted successfully");
		} catch (error) {
			console.error("Error deleting logo:", error);
			toast.error("Failed to delete logo");
		}
	};

	const triggerFileInput = () => {
		fileInputRef.current?.click();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === "Enter" || e.key === " ") {
			if (!previewUrl && !currentLogoUrl) {
				triggerFileInput();
			}
		}
	};

	return (
		<div className="flex flex-col space-y-4">
			<div
				className={cn(
					"flex justify-center items-center bg-muted border rounded-md w-full h-32 overflow-hidden relative group transition-colors duration-200 ease-in-out",
					!previewUrl && !currentLogoUrl ? "cursor-pointer" : "",
				)}
				onClick={!previewUrl && !currentLogoUrl ? triggerFileInput : undefined}
				onKeyDown={handleKeyDown}
				tabIndex={!previewUrl && !currentLogoUrl ? 0 : -1} // Make it focusable only when clickable
				role={!previewUrl && !currentLogoUrl ? "button" : undefined} // Add role for semantics
				aria-label={!previewUrl && !currentLogoUrl ? "Upload logo" : undefined}
			>
				{previewUrl ? (
					<img
						src={previewUrl}
						alt={`${name} logo preview`}
						className="max-w-full max-h-full object-contain transition-opacity duration-300 ease-in-out"
					/>
				) : currentLogoUrl ? (
					<img
						src={currentLogoUrl}
						alt={`${name} logo`}
						className="max-w-full max-h-full object-contain"
					/>
				) : (
					<div className="flex flex-col items-center group-hover:opacity-70 text-muted-foreground transition-opacity duration-200 ease-in-out pointer-events-none">
						<UploadCloud className="mb-1 w-8 h-8" />
						<span className="text-sm">Click to upload</span>
					</div>
				)}
			</div>

			<div className="flex space-x-2">
				<div className="relative flex-1 space-y-2">
					<Label htmlFor={`file-${id}`} className="sr-only">
						Upload Logo
					</Label>
					<Input
						id={`file-${id}`}
						type="file"
						accept="image/*"
						onChange={handleFileChange}
						ref={fileInputRef}
						className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" // Hidden input
					/>
					{/* Custom styled button to trigger the hidden input */}
					<Button
						variant="outline"
						className="w-full transition-colors duration-200 ease-in-out"
						onClick={triggerFileInput}
						aria-hidden="true" // Hide from accessibility tree as it's decorative
					>
						{selectedFile ? selectedFile.name : "Choose file..."}
					</Button>
				</div>
				<div className="flex items-end space-x-2">
					<Button
						variant="outline"
						onClick={(e) => {
							e.stopPropagation();
							onPasteClick();
						}}
						title="Paste from clipboard"
						className="px-0 aspect-square transition-colors duration-200 ease-in-out"
					>
						<Clipboard size={20} />
					</Button>
					{currentLogoUrl && onDelete && (
						<Button
							variant="outline"
							onClick={(e) => {
								e.stopPropagation();
								handleDelete();
							}}
							disabled={isDeleting}
							title="Delete logo"
							className="hover:bg-red-50 px-0 aspect-square text-red-600 hover:text-red-700 transition-colors duration-200 ease-in-out"
						>
							<Trash2 size={20} />
						</Button>
					)}
				</div>
			</div>

			{selectedFile && (
				<Button
					onClick={(e) => {
						e.stopPropagation();
						handleUpload();
					}}
					disabled={isUploading}
					className="hover:bg-primary/90 w-full transition-colors duration-200 ease-in-out"
				>
					{isUploading ? "Uploading..." : "Upload Logo"}
				</Button>
			)}
		</div>
	);
}
